import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

// Like Validators.required, but also rejects a whitespace-only string — mirrors
// HearingLanguageSupportStepComponent's own copy of this helper (not shared/exported
// there either; each step component that needs it defines its own).
function requiredNonBlankValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value
  if (value === null || value === undefined) {
    return { required: true }
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return { required: true }
  }
  return null
}

type GuidanceKey =
  | 'communityVisitsGuidance'
  | 'collaborativeCareCfssGuidance'
  | 'collaborativeCareAudGuidance'
  | 'additionalCommunicationAssessmentGuidance'
  | 'startingSchoolReportGuidance'
  | 'socialEmotionalAssessGuidance'
  | 'speechPerceptionTestingGuidance'
  | 'annualSpeechPerceptionGuidance'
  | 'talkTogetherGuidance'

type SelectionKey =
  | 'communityVisitsQty'
  | 'travelQty'
  | 'collaborativeCareCfssQty'
  | 'collaborativeCareAudQty'
  | 'additionalCommunicationAssessmentQty'
  | 'startingSchoolReport'
  | 'socialEmotionalAssess'
  | 'speechPerceptionTesting'
  | 'annualSpeechPerception'
  | 'talkTogether'

// Each additional-service option and its own "Booking guidance" control. The guidance
// is required only while its option is selected (quantity > 0 / toggle on).
const GUIDANCE_ITEMS: ReadonlyArray<{
  selection: SelectionKey
  guidance: GuidanceKey
  requiredMessageKey: string
}> = [
  {
    selection: 'communityVisitsQty',
    guidance: 'communityVisitsGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_COMMUNITY_VISITS',
  },
  {
    selection: 'collaborativeCareCfssQty',
    guidance: 'collaborativeCareCfssGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_COLLABORATIVE_CARE_CFSS',
  },
  {
    selection: 'collaborativeCareAudQty',
    guidance: 'collaborativeCareAudGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_COLLABORATIVE_CARE_AUD',
  },
  {
    selection: 'additionalCommunicationAssessmentQty',
    guidance: 'additionalCommunicationAssessmentGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_ADDITIONAL_COMMUNICATION_ASSESSMENT',
  },
  {
    selection: 'startingSchoolReport',
    guidance: 'startingSchoolReportGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_STARTING_SCHOOL_REPORT',
  },
  {
    selection: 'socialEmotionalAssess',
    guidance: 'socialEmotionalAssessGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_SOCIAL_EMOTIONAL_ASSESS',
  },
  {
    selection: 'speechPerceptionTesting',
    guidance: 'speechPerceptionTestingGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_SPEECH_PERCEPTION_TESTING',
  },
  {
    selection: 'annualSpeechPerception',
    guidance: 'annualSpeechPerceptionGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_ANNUAL_SPEECH_PERCEPTION',
  },
  {
    selection: 'talkTogether',
    guidance: 'talkTogetherGuidance',
    requiredMessageKey:
      'SERVICE_PLAN.VALIDATION.BOOKING_GUIDANCE_REQUIRED_TALK_TOGETHER',
  },
]

// Step 7 — Additional support services (story e8-6), shown unconditionally for every
// plan type. Ten pre-existing payload fields plus one new one (annualSpeechPerceptionReason),
// organised into 5 named sub-groups. Two independent gating concerns on one FormGroup:
// a real conditionally-required Validators.required cascade (age-4+ rule, drives
// isLiveValid/getValidationErrors like every other required-field step), and a separate
// one-shot "warning-hold" on Continue (travel/community mismatch, rule 15) that never
// touches form validity and is never surfaced through getValidationErrors().
@Component({
  selector: 'app-service-plan-additional-services-step',
  templateUrl: './additional-services-step.component.html',
  styleUrls: ['./additional-services-step.component.scss'],
  standalone: false,
})
export class AdditionalServicesStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  @Input() clientAgeYears: number | null = null

  form!: FormGroup<{
    communityVisitsQty: FormControl<number>
    travelQty: FormControl<number>
    collaborativeCareCfssQty: FormControl<number>
    collaborativeCareAudQty: FormControl<number>
    additionalCommunicationAssessmentQty: FormControl<number>
    startingSchoolReport: FormControl<boolean>
    socialEmotionalAssess: FormControl<boolean>
    speechPerceptionTesting: FormControl<boolean>
    annualSpeechPerception: FormControl<boolean>
    annualSpeechPerceptionReason: FormControl<string | null>
    talkTogether: FormControl<boolean>
    communityVisitsGuidance: FormControl<string | null>
    collaborativeCareCfssGuidance: FormControl<string | null>
    collaborativeCareAudGuidance: FormControl<string | null>
    additionalCommunicationAssessmentGuidance: FormControl<string | null>
    startingSchoolReportGuidance: FormControl<string | null>
    socialEmotionalAssessGuidance: FormControl<string | null>
    speechPerceptionTestingGuidance: FormControl<string | null>
    annualSpeechPerceptionGuidance: FormControl<string | null>
    talkTogetherGuidance: FormControl<string | null>
  }>

  introText = ''
  groupCommunityHeading = ''
  groupCommunityPrompt = ''
  groupTeamHeading = ''
  groupSpeechHeading = ''
  groupHearingHeading = ''
  groupHearingPrompt = ''
  groupGroupsHeading = ''

  communityVisitsLabel = ''
  travelLabel = ''
  collaborativeCareCfssLabel = ''
  collaborativeCareAudLabel = ''
  additionalCommunicationAssessmentLabel = ''
  startingSchoolReportLabel = ''
  socialEmotionalAssessLabel = ''
  speechPerceptionTestingLabel = ''
  annualSpeechPerceptionLabel = ''
  annualSpeechPerceptionHint = ''
  annualSpeechPerceptionReasonLabel = ''
  annualSpeechPerceptionReasonControlId = ''
  talkTogetherLabel = ''
  bookingGuidanceLabel = ''
  travelWarningMessage = ''

  showTravelWarning = false

  private subscription = new Subscription()

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.resolveLabels()

    const payload = this.state.getPayload().additionalServices

    // Age-4+ auto-select (AC2) — a one-time seed applied before the rest of the
    // form's initial values are read, guarded by a session-scoped flag that lives
    // on ServicePlanStateService (not the payload — see Task 2). Must never
    // re-apply on a later revisit, whatever the clinician has since chosen.
    let initialAnnualSpeechPerception = payload.annualSpeechPerception
    let initialSpeechPerceptionTesting = payload.speechPerceptionTesting
    if (!this.state.hasAppliedAnnualSpeechPerceptionAutoSelect()) {
      if (this.clientAgeYears !== null && this.clientAgeYears >= 4) {
        initialAnnualSpeechPerception = true
        initialSpeechPerceptionTesting = false
      }
      this.state.markAnnualSpeechPerceptionAutoSelectApplied()
    }

    // Rule 14's OR-only exclusivity is otherwise only enforced going forward by the
    // valueChanges cascade below — sanitize a payload that somehow already has both
    // set (e.g. a future write path that bypasses this component) before it's ever
    // seeded into the form, rather than rendering both toggles selected at once.
    if (initialAnnualSpeechPerception && initialSpeechPerceptionTesting) {
      initialSpeechPerceptionTesting = false
    }

    this.form = new FormGroup({
      communityVisitsQty: new FormControl<number>(payload.communityVisitsQty, {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(8)],
      }),
      travelQty: new FormControl<number>(payload.travelQty, {
        nonNullable: true,
        validators: [Validators.min(0)],
      }),
      collaborativeCareCfssQty: new FormControl<number>(
        payload.collaborativeCareCfssQty,
        { nonNullable: true, validators: [Validators.min(0)] },
      ),
      collaborativeCareAudQty: new FormControl<number>(
        payload.collaborativeCareAudQty,
        { nonNullable: true, validators: [Validators.min(0)] },
      ),
      additionalCommunicationAssessmentQty: new FormControl<number>(
        payload.additionalCommunicationAssessmentQty,
        { nonNullable: true, validators: [Validators.min(0)] },
      ),
      startingSchoolReport: new FormControl<boolean>(
        payload.startingSchoolReport,
        { nonNullable: true },
      ),
      socialEmotionalAssess: new FormControl<boolean>(
        payload.socialEmotionalAssess,
        { nonNullable: true },
      ),
      speechPerceptionTesting: new FormControl<boolean>(
        initialSpeechPerceptionTesting,
        { nonNullable: true },
      ),
      annualSpeechPerception: new FormControl<boolean>(
        initialAnnualSpeechPerception,
        { nonNullable: true },
      ),
      annualSpeechPerceptionReason: new FormControl<string | null>(
        payload.annualSpeechPerceptionReason,
      ),
      talkTogether: new FormControl<boolean>(payload.talkTogether, {
        nonNullable: true,
      }),
      communityVisitsGuidance: new FormControl<string | null>(
        payload.communityVisitsGuidance,
      ),
      collaborativeCareCfssGuidance: new FormControl<string | null>(
        payload.collaborativeCareCfssGuidance,
      ),
      collaborativeCareAudGuidance: new FormControl<string | null>(
        payload.collaborativeCareAudGuidance,
      ),
      additionalCommunicationAssessmentGuidance: new FormControl<string | null>(
        payload.additionalCommunicationAssessmentGuidance,
      ),
      startingSchoolReportGuidance: new FormControl<string | null>(
        payload.startingSchoolReportGuidance,
      ),
      socialEmotionalAssessGuidance: new FormControl<string | null>(
        payload.socialEmotionalAssessGuidance,
      ),
      speechPerceptionTestingGuidance: new FormControl<string | null>(
        payload.speechPerceptionTestingGuidance,
      ),
      annualSpeechPerceptionGuidance: new FormControl<string | null>(
        payload.annualSpeechPerceptionGuidance,
      ),
      talkTogetherGuidance: new FormControl<string | null>(
        payload.talkTogetherGuidance,
      ),
    })

    this.applyReasonValidatorCascade()
    this.applyGuidanceValidatorCascade()
    this.writePayload()

    const controls = this.form.controls

    // OR-only cascade (rule 14) — each direction subscribes directly to its own
    // trigger control, clearing the other control silently (emitEvent: false) so
    // it does not re-trigger the opposite subscription.
    this.subscription.add(
      controls.speechPerceptionTesting.valueChanges.subscribe((value) => {
        if (value === true && controls.annualSpeechPerception.value === true) {
          controls.annualSpeechPerception.setValue(false, {
            emitEvent: false,
          })
        }
      }),
    )
    this.subscription.add(
      controls.annualSpeechPerception.valueChanges.subscribe((value) => {
        if (value === true && controls.speechPerceptionTesting.value === true) {
          controls.speechPerceptionTesting.setValue(false, {
            emitEvent: false,
          })
        }
      }),
    )

    // Travel/community warning-hold (rule 15) — clears immediately on any change
    // to either quantity, without waiting for the next Continue click (AC5).
    this.subscription.add(
      controls.travelQty.valueChanges.subscribe(() => {
        this.showTravelWarning = false
      }),
    )
    this.subscription.add(
      controls.communityVisitsQty.valueChanges.subscribe(() => {
        this.showTravelWarning = false
      }),
    )

    this.subscription.add(
      this.form.valueChanges.subscribe(() => {
        this.applyReasonValidatorCascade()
        this.applyGuidanceValidatorCascade()
        this.writePayload()
      }),
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  // Rule 9, per option: a guidance control is required only while its own option is
  // selected (quantity > 0 or toggle on).
  isGuidanceRequired(guidance: GuidanceKey): boolean {
    if (!this.form) {
      return false
    }
    const item = GUIDANCE_ITEMS.find((i) => i.guidance === guidance)
    if (!item) {
      return false
    }
    const value = this.form.controls[item.selection].value
    return typeof value === 'number' ? value > 0 : value === true
  }

  // Exposes the same condition applyReasonValidatorCascade() uses, bound to the
  // template's *ngIf and the reason textarea's `required` input.
  get requiresAnnualSpeechPerceptionReason(): boolean {
    if (!this.form) {
      return false
    }
    return (
      this.clientAgeYears !== null &&
      this.clientAgeYears >= 4 &&
      this.form.controls.annualSpeechPerception.value === false
    )
  }

  // ServicePlanStepComponent contract — the shell's Continue button reads this
  // directly (non-mutating). Reflects only the conditionally-required reason field
  // — the travel/community hold never touches form validity (it must not visually
  // disable Continue, only hold on click).
  get isLiveValid(): boolean {
    return this.form?.valid ?? false
  }

  isValid(): boolean {
    this.form.markAllAsTouched()
    if (!this.form.valid) {
      // A missing required reason must still leave showTravelWarning unset so the
      // banner doesn't appear for an unrelated failure reason.
      return false
    }

    const mismatch =
      this.form.controls.travelQty.value === 0 &&
      this.form.controls.communityVisitsQty.value > 0

    if (mismatch && !this.showTravelWarning) {
      this.showTravelWarning = true
      return false
    }

    this.showTravelWarning = false
    return true
  }

  getValidationErrors(): ServicePlanFieldError[] {
    // The travel/community banner is deliberately never surfaced here — it is the
    // step's own template output (showTravelWarning), not a shared error-summary entry.
    const errors: ServicePlanFieldError[] = []
    if (this.form.controls.annualSpeechPerceptionReason.invalid) {
      errors.push({
        controlId: this.annualSpeechPerceptionReasonControlId,
        messageKey:
          'SERVICE_PLAN.VALIDATION.ANNUAL_SPEECH_PERCEPTION_REASON_REQUIRED',
      })
    }
    for (const item of GUIDANCE_ITEMS) {
      if (this.form.controls[item.guidance].invalid) {
        errors.push({
          controlId: this.guidanceControlId(item.guidance),
          messageKey: item.requiredMessageKey,
        })
      }
    }
    return errors
  }

  // DOM id of a guidance input — the shared label is identical for every option, so the
  // id is derived from the control name instead (passed to TextInputComponent as controlId).
  guidanceControlId(guidance: GuidanceKey): string {
    return `service-plan-additional-services-${guidance}`
  }

  private resolveLabels(): void {
    const t = (key: string) => this.translate.instant(key)

    this.introText = t('SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.INTRO')
    this.groupCommunityHeading = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_COMMUNITY_HEADING',
    )
    this.groupCommunityPrompt = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_COMMUNITY_PROMPT',
    )
    this.groupTeamHeading = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_TEAM_HEADING',
    )
    this.groupSpeechHeading = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_SPEECH_HEADING',
    )
    this.groupHearingHeading = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_HEARING_HEADING',
    )
    this.groupHearingPrompt = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_HEARING_PROMPT',
    )
    this.groupGroupsHeading = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.GROUP_GROUPS_HEADING',
    )

    this.communityVisitsLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.COMMUNITY_VISITS_LABEL',
    )
    this.travelLabel = t('SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.TRAVEL_LABEL')
    this.collaborativeCareCfssLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.COLLABORATIVE_CARE_CFSS_LABEL',
    )
    this.collaborativeCareAudLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.COLLABORATIVE_CARE_AUD_LABEL',
    )
    this.additionalCommunicationAssessmentLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.ADDITIONAL_COMMUNICATION_ASSESSMENT_LABEL',
    )
    this.startingSchoolReportLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.STARTING_SCHOOL_REPORT_LABEL',
    )
    this.socialEmotionalAssessLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.SOCIAL_EMOTIONAL_ASSESS_LABEL',
    )
    this.speechPerceptionTestingLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.SPEECH_PERCEPTION_TESTING_LABEL',
    )
    this.annualSpeechPerceptionLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.ANNUAL_SPEECH_PERCEPTION_LABEL',
    )
    this.annualSpeechPerceptionHint = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.ANNUAL_SPEECH_PERCEPTION_HINT',
    )
    this.annualSpeechPerceptionReasonLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.ANNUAL_SPEECH_PERCEPTION_REASON_LABEL',
    )
    this.annualSpeechPerceptionReasonControlId = LabelToId(
      this.annualSpeechPerceptionReasonLabel,
    )
    this.talkTogetherLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.TALK_TOGETHER_LABEL',
    )
    this.bookingGuidanceLabel = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.BOOKING_GUIDANCE_LABEL',
    )
    this.travelWarningMessage = t(
      'SERVICE_PLAN.STEP.ADDITIONAL_SERVICES.TRAVEL_WARNING_MESSAGE',
    )
  }

  // Conditionally-required reason validator (AC3), cascade pattern copied from
  // HearingLanguageSupportStepComponent's applyValidatorCascade-style method — run
  // once in ngOnInit and again on every valueChanges tick.
  private applyReasonValidatorCascade(): void {
    const control = this.form.controls.annualSpeechPerceptionReason
    if (this.requiresAnnualSpeechPerceptionReason) {
      control.setValidators(requiredNonBlankValidator)
    } else {
      control.clearValidators()
      if (control.value !== null) {
        control.setValue(null, { emitEvent: false })
      }
    }
    control.updateValueAndValidity({ emitEvent: false })
  }

  // Each guidance control is required while its option is selected, and cleared +
  // made optional when it is not.
  private applyGuidanceValidatorCascade(): void {
    for (const item of GUIDANCE_ITEMS) {
      const control = this.form.controls[item.guidance]
      if (this.isGuidanceRequired(item.guidance)) {
        control.setValidators(requiredNonBlankValidator)
      } else {
        control.clearValidators()
        if (control.value !== null) {
          control.setValue(null, { emitEvent: false })
        }
      }
      control.updateValueAndValidity({ emitEvent: false })
    }
  }

  private writePayload(): void {
    const value = this.form.getRawValue()
    this.state.updatePayload({
      additionalServices: {
        ...this.state.getPayload().additionalServices,
        communityVisitsQty: value.communityVisitsQty,
        travelQty: value.travelQty,
        collaborativeCareCfssQty: value.collaborativeCareCfssQty,
        collaborativeCareAudQty: value.collaborativeCareAudQty,
        additionalCommunicationAssessmentQty:
          value.additionalCommunicationAssessmentQty,
        startingSchoolReport: value.startingSchoolReport,
        socialEmotionalAssess: value.socialEmotionalAssess,
        speechPerceptionTesting: value.speechPerceptionTesting,
        annualSpeechPerception: value.annualSpeechPerception,
        annualSpeechPerceptionReason: value.annualSpeechPerceptionReason,
        talkTogether: value.talkTogether,
        communityVisitsGuidance: value.communityVisitsGuidance,
        collaborativeCareCfssGuidance: value.collaborativeCareCfssGuidance,
        collaborativeCareAudGuidance: value.collaborativeCareAudGuidance,
        additionalCommunicationAssessmentGuidance:
          value.additionalCommunicationAssessmentGuidance,
        startingSchoolReportGuidance: value.startingSchoolReportGuidance,
        socialEmotionalAssessGuidance: value.socialEmotionalAssessGuidance,
        speechPerceptionTestingGuidance: value.speechPerceptionTestingGuidance,
        annualSpeechPerceptionGuidance: value.annualSpeechPerceptionGuidance,
        talkTogetherGuidance: value.talkTogetherGuidance,
      },
    })
  }
}
